import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { Box, TextField, CircularProgress, Snackbar, Alert } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Cookie from "js-cookie";
import URLs from "constants/urls";

import {
  inputStyle,
  StyledButton,
  StyledTypography,
  StyledAgGridContainer,
  StyledTableContainer,
} from "components/StyledComponents/styledComponents.js";
import { useMaterialUIController } from "context";
export default function TimeTrack() {
  const [payPeriods, setPayPeriods] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState("");
  const [selectedPayPeriodId, setSelectedPayPeriodId] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedEmployeeRecordId, setSelectedEmployeeRecordId] = useState("");
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [alertType, setAlertType] = useState("success");
  const [message, setMessage] = useState("");
  const hrms_token = Cookie.get("hrms_token");
  const MInput = inputStyle();
  const [controller] = useMaterialUIController();
  const { user } = controller;

  useEffect(() => {
    fetchPayPeriods();
    fetchEmployees();
  }, []);

  const fetchPayPeriods = async () => {
    try {
      const response = await fetch(`${URLs.hrmsGetPayPeriods}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch pay periods");
      const data = await response.json();
      setPayPeriods(data);
    } catch (error) {
      setMessage("Failed to fetch pay periods");
      setAlertType("error");
      setOpen(true);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch(
        `${URLs.getalldatabyroleid}?roleId=${user.roleId}&employeeRecordId=${user.employeeRecordId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${hrms_token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch employees");
      const data = await response.json();
      setEmployees(data);
    } catch (error) {
      setMessage("Failed to fetch employees");
      setAlertType("error");
      setOpen(true);
    }
  };

  const handleDisplay = async () => {
    if (!selectedPayPeriodId || !selectedEmployeeRecordId) {
      setMessage("Please select both pay period and employee");
      setAlertType("error");
      setOpen(true);
      return;
    }
    setLoading(true);
    setAttendanceData([]);
    try {
      const url = `${URLs.hrmsGetlogsby}/${selectedPayPeriodId}/${selectedEmployeeRecordId}?payPeriodId=${selectedPayPeriodId}&employeeRecordId=${selectedEmployeeRecordId}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch logs");
      const data = await response.json();
      // Map data to use firstIn and lastOut fields
      const mappedData = Array.isArray(data)
        ? data.map((item) => {
            // Extract date from firstIn (if present)
            let dateStr = "";
            if (item.firstIn) {
              const dt = new Date(item.firstIn);
              if (!isNaN(dt.getTime())) {
                const day = String(dt.getDate()).padStart(2, "0");
                const month = String(dt.getMonth() + 1).padStart(2, "0");
                const year = dt.getFullYear();
                dateStr = `${day}-${month}-${year}`;
              }
            }
            return {
              bioEmployeeId: item.bioEmployeeId,
              employeeName: item.employeeName,
              firstIn: item.firstIn,
              lastOut: item.lastOut,
              duration: item.duration,
              date: dateStr,
            };
          })
        : [];
      setAttendanceData(mappedData);
    } catch (error) {
      setMessage("Failed to fetch logs");
      setAlertType("error");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePayPeriodChange = (e) => {
    const periodName = e.target.value;
    setSelectedPayPeriod(periodName);
    const found = payPeriods.find((p) => p.payPeriodName === periodName);
    setSelectedPayPeriodId(found ? found.payPeriodId : "");
  };

  const handleEmployeeChange = (e) => {
    const empName = e.target.value;
    setSelectedEmployee(empName);
    const found = employees.find(
      (emp) => (emp.employeeName || emp.firstName + " " + emp.lastName) === empName
    );
    setSelectedEmployeeRecordId(found ? found.employeeRecordId || found.id : "");
  };

  const columns = [
    {
      headerName: "Employee Code",
      field: "bioEmployeeId",
      flex: 1,
    },
    {
      headerName: "Employee Name",
      field: "employeeName",
      flex: 1.5,
    },
    {
      headerName: "Date",
      field: "date",
      flex: 1.1,
      valueFormatter: (params) => params.value || "",
    },
    {
      headerName: "First In",
      field: "firstIn",
      flex: 1.5,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const dt = new Date(params.value);
        if (isNaN(dt.getTime())) return params.value;
        const day = String(dt.getDate()).padStart(2, "0");
        const month = String(dt.getMonth() + 1).padStart(2, "0");
        const year = dt.getFullYear();
        const time = dt
          .toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          })
          .toUpperCase();
        return `${time}`;
      },
    },
    {
      headerName: "Last Out",
      field: "lastOut",
      flex: 1.5,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const dt = new Date(params.value);
        if (isNaN(dt.getTime())) return params.value;
        const day = String(dt.getDate()).padStart(2, "0");
        const month = String(dt.getMonth() + 1).padStart(2, "0");
        const year = dt.getFullYear();
        const time = dt
          .toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          })
          .toUpperCase();
        return `${time}`;
      },
    },
    {
      headerName: "Duration",
      field: "duration",
      flex: 1.1,
      valueFormatter: (params) => params.value || "",
    },
  ];

  function formatTime(dateTimeString) {
    if (!dateTimeString) return "";
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return "";
    return date
      .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })
      .toUpperCase();
  }

  return (
    <DashboardLayout>
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", mt: 0 }}>
        <StyledTableContainer
          container
          spacing={1}
          sx={{
            display: "flex",
            backgroundColor: "#ffffff",
            minHeight: "500px",
            borderRadius: "15px",
            overflow: "auto",
            width: "100%",
            mb: 2,
          }}
        >
          <Box sx={{ width: "100%", p: 3 }}>
            {/* <StyledTypography>Time Tracking</StyledTypography> */}
            <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                select
                label="Select Pay Period"
                value={selectedPayPeriod}
                onChange={handlePayPeriodChange}
                sx={{ minWidth: 200, ...MInput.b }}
                SelectProps={{ native: true }}
                InputLabelProps={{
                  sx: { color: MInput.a },
                }}
              >
                <option value=""></option>
                {payPeriods.map((period) => (
                  <option key={period.payPeriodId} value={period.payPeriodName}>
                    {period.payPeriodName}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                label="Select Employee"
                value={selectedEmployee}
                onChange={handleEmployeeChange}
                sx={{ minWidth: 200, ...MInput.b }}
                SelectProps={{ native: true }}
                InputLabelProps={{
                  sx: { color: MInput.a },
                }}
              >
                <option value=""></option>
                {employees.map((emp) => (
                  <option
                    key={emp.employeeId || emp.id}
                    value={emp.employeeName || emp.firstName + " " + emp.lastName}
                  >
                    {emp.employeeName || emp.firstName + " " + emp.lastName}
                  </option>
                ))}
              </TextField>
              <StyledButton
                onClick={handleDisplay}
                disabled={!selectedPayPeriodId || !selectedEmployeeRecordId || loading}
                sx={{ height: 40 }}
              >
                Display
              </StyledButton>
            </Box>
            <StyledAgGridContainer
              component={Box}
              className="ag-theme-alpine"
              sx={{
                flexGrow: 1,
                backgroundColor: "transparent",
                height: "calc(100vh - 260px)",
                width: "100%",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <AgGridReact
                  rowData={attendanceData}
                  columnDefs={columns}
                  pagination={true}
                  paginationPageSize={10}
                  defaultColDef={{ resizable: true, filter: true }}
                  overlayNoRowsTemplate="<span>No attendance records found</span>"
                />
              )}
            </StyledAgGridContainer>
          </Box>
        </StyledTableContainer>
        <Snackbar
          open={open}
          autoHideDuration={5000}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={() => setOpen(false)} severity={alertType} sx={{ width: "100%" }}>
            {message}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
}
